import React, { useState, useEffect, useRef, useContext } from "react";
import {
  FaBars,
  FaSearch,
  FaUserCircle,
  FaBell,
  FaUsers,
  FaHome,
  FaCalendarAlt,
  FaNewspaper,
  FaBook,
  FaUsers as FaCommittee,
  FaBlog,
  FaImages,
  FaInfoCircle,
  FaSignOutAlt,
  FaTimes
} from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { UserContext } from "../../Context/usercontext";
import { toast } from "react-toastify";
import Cookies from "js-cookie";
import { motion, AnimatePresence } from "framer-motion";
import logo from "./logo.png"

const MobileHeader = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const debounceTimeout = useRef(null);
  const searchInputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(!!Cookies.get("userToken"));
  const { logoutUser } = useContext(UserContext);

  // Navigation items
  const navItems = [
    { path: "home", label: "Home", icon: FaHome },
    { path: "event", label: "Event", icon: FaCalendarAlt },
    { path: "notice", label: "Notice", icon: FaNewspaper },
    { path: "courses", label: "Courses", icon: FaBook },
    { path: "committee", label: "Committee", icon: FaCommittee },
    { path: "blog", label: "Blog", icon: FaBlog },
    { path: "gallery", label: "Gallery", icon: FaImages },
    { path: "about", label: "About", icon: FaInfoCircle },
  ];

  const fetchSuggestions = async (value) => {
    if (!value) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/apis/admin/globalSearch/${value}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });
      const result = await res.json();
      if (result.success && result.data) {
        const filtered = result.data.filter((item) => {
          const type = item.type;
          const data = item.data;
          if (type === "UpcomingEvent" || type === "Notice") {
            return data?.title?.toLowerCase().includes(value.toLowerCase());
          }
          if (type === "UnderGraduateMenu" || type === "PostGraduateMenu") {
            return data?.menuName?.toLowerCase().includes(value.toLowerCase());
          }
          if (type === "AdmissionMenu") {
            return data?.admissionMenuName?.toLowerCase().includes(value.toLowerCase());
          }
          if (type === "UnderGraduate" || type === "PostGraduate") {
            return data?.courseName?.toLowerCase().includes(value.toLowerCase());
          }
          if (type === "AdmissionType") {
            return data?.admissionName?.toLowerCase().includes(value.toLowerCase());
          }
          return false;
        }).map((item) => ({
          ...item.data,
          type: item.type,
        }));
        setSuggestions(filtered);
      } else {
        setSuggestions([]);
      }
    } catch (error) {
      console.error("Search failed", error);
      setSuggestions([]);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    if (value.trim() === "") {
      setSuggestions([]);
      return;
    }
    debounceTimeout.current = setTimeout(() => {
      fetchSuggestions(value);
    }, 600);
  };

  const handleSelectSuggestion = (eventData, e) => {
    e.stopPropagation();
    setSearchTerm(eventData.title || eventData.menuName || eventData.admissionMenuName || eventData.courseName || eventData.admissionName || "");
    setSuggestions([]);
    setIsSearchOpen(false);
    navigateToEvent(eventData);
  };

  const navigateToEvent = (eventData) => {
    if (eventData.type === "Notice") navigate("/notice");
    else if (eventData.type === "UpcomingEvent") navigate("/event");
    else if (eventData.type === "UnderGraduateMenu") navigate(`/graduate-course-details/${eventData.underGraduateCourseId}`);
    else if (eventData.type === "PostGraduateMenu") navigate(`/postgraduate-course-details/${eventData.postGraduateCourseId}`);
    else if (eventData.type === "AdmissionMenu") navigate(`/admission-details/${eventData.admissionTypeId}`);
    else if (eventData.type === "UnderGraduate") navigate(`/courses`);
    else if (eventData.type === "PostGraduate") navigate(`/courses`);
    else if (eventData.type === "AdmissionType") navigate(`/courses`);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchInputRef.current && !searchInputRef.current.contains(event.target) && suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setIsSearchOpen(false);
        setSuggestions([]);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && searchTerm.trim() !== "") {
      fetchSuggestions(searchTerm);
    }
  };

  const handleLogout = async () => {
    if (isLoggedIn) {
      setIsLoading(true);
      try {
        await new Promise(resolve => setTimeout(resolve, 1500));
        logoutUser();
        setIsLoggedIn(false);
        toast.success("Successfully logged out!");
        navigate("/login");
      } catch (error) {
        toast.error("Logout failed. Please try again.");
      } finally {
        setIsLoading(false);
      }
    } else {
      navigate("/login");
    }
  };

  // Animation variants
  const menuVariants = {
    hidden: { opacity: 0, y: -20, height: 0 },
    visible: {
      opacity: 1,
      y: 0,
      height: "auto",
      transition: { duration: 0.3, ease: "easeOut" }
    },
    exit: {
      opacity: 0,
      y: -20,
      height: 0,
      transition: { duration: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: { delay: i * 0.05 }
    })
  };

  return (
    <>
      {/* Loading Overlay */}
      <AnimatePresence>
        {isLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 backdrop-blur-md"
          >
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              className="bg-white rounded-2xl p-6 shadow-2xl"
            >
              <div className="w-12 h-12 border-4 border-amber-600 border-t-transparent rounded-full animate-spin" />
              <p className="text-gray-700 font-semibold mt-4">Logging out...</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Header - Updated colors to match desktop header */}
      <header className="w-full bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800 shadow-lg px-3 py-2 flex items-center justify-between sm:hidden sticky top-0 z-40">
        {/* Logo - Fixed to show the imported logo */}
        <Link to="/home" className="relative group">
          {/* Logo glow effect */}
          <div className="absolute -inset-1 bg-gradient-to-r from-amber-400 to-orange-400 rounded-lg opacity-0 group-hover:opacity-30 blur transition duration-300"></div>
          
          {/* Logo container */}
          <div className="relative flex items-center space-x-1 bg-white/5 backdrop-blur-sm px-2 py-1 rounded-lg border border-white/10 group-hover:border-white/20 transition-all duration-300">
            {/* Logo image with glow effect */}
            <div className="relative">
              <div className="absolute inset-0 bg-white/20 rounded-full blur-sm group-hover:bg-white/30 transition"></div>
              <img 
                src={logo} 
                alt="Alumni Connect" 
                className="relative w-6 h-6 object-contain filter drop-shadow-lg group-hover:scale-110 transition-transform duration-300" 
              />
            </div>
            
            {/* Logo text */}
            <div className="flex flex-col">
              <span className="text-white font-bold text-xs tracking-wide leading-tight">
                Alumni
              </span>
              <span className="text-amber-300 text-[8px] font-medium -mt-0.5 leading-tight">
                Connect
              </span>
            </div>
          </div>
        </Link>

        <div className="flex items-center space-x-3">
          {/* Search Icon */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => setIsSearchOpen(!isSearchOpen)}
            className="text-slate-300 hover:text-amber-300 transition"
          >
            <FaSearch size={18} />
          </motion.button>

          {/* Connections Icon */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => navigate("/connections")}
            className="text-slate-300 hover:text-amber-300 transition"
          >
            <FaUsers size={18} />
          </motion.button>

          {/* Notifications Icon */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => navigate("/notifications")}
            className="text-slate-300 hover:text-amber-300 transition relative"
          >
            <FaBell size={18} />
            {/* Optional: Add notification badge if you have unread count */}
            {/* {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-amber-500 text-white text-[8px] rounded-full h-3.5 w-3.5 flex items-center justify-center">
                {unreadCount}
              </span>
            )} */}
          </motion.button>

          {/* Profile Icon */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => navigate("/profile")}
            className="text-slate-300 hover:text-amber-300 transition"
          >
            <FaUserCircle size={20} />
          </motion.button>

          {/* Menu Toggle */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => setMenuOpen(!menuOpen)}
            className="text-slate-300 hover:text-amber-300 transition"
          >
            {menuOpen ? <FaTimes size={20} /> : <FaBars size={20} />}
          </motion.button>
        </div>
      </header>

      {/* Search Bar - Updated colors */}
      <AnimatePresence>
        {isSearchOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="relative w-full bg-white/95 backdrop-blur-md p-3 border-b border-slate-200"
          >
            <div className="relative">
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search courses, events, alumni..."
                value={searchTerm}
                onChange={handleSearch}
                onKeyDown={handleKeyDown}
                className="w-full p-2.5 pl-9 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
              />
              <FaSearch className="absolute left-3 top-3 text-slate-400" />

              {/* Suggestions - Updated colors */}
              <AnimatePresence>
                {suggestions.length > 0 && (
                  <motion.ul
                    ref={suggestionsRef}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute left-0 right-0 bg-white shadow-xl rounded-lg mt-1 z-50 max-h-60 overflow-y-auto border border-slate-200"
                  >
                    {suggestions.map((sugg, index) => (
                      <motion.li
                        key={sugg._id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        onClick={(e) => handleSelectSuggestion(sugg, e)}
                        className="px-3 py-2.5 hover:bg-amber-50 cursor-pointer border-b last:border-b-0"
                      >
                        <p className="text-gray-700 text-sm font-medium">
                          {sugg.title || sugg.menuName || sugg.admissionMenuName || sugg.courseName || sugg.admissionName}
                        </p>
                        <p className="text-xs text-amber-600 mt-0.5">{sugg.type}</p>
                      </motion.li>
                    ))}
                  </motion.ul>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Navigation Menu - Updated colors */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            variants={menuVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="bg-white/95 backdrop-blur-md shadow-xl rounded-b-lg mx-3 mt-1 overflow-hidden border border-slate-200"
          >
            <div className="p-3 flex flex-col space-y-0.5">
              {navItems.map((item, index) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={item.path}
                    custom={index}
                    variants={itemVariants}
                  >
                    <Link
                      to={`/${item.path}`}
                      className="flex items-center space-x-3 px-3 py-2.5 rounded-lg hover:bg-amber-50 transition group"
                      onClick={() => setMenuOpen(false)}
                    >
                      <div className="w-7 h-7 bg-slate-100 rounded-lg flex items-center justify-center group-hover:bg-amber-100 transition">
                        <Icon className="w-3.5 h-3.5 text-slate-600 group-hover:text-amber-600" />
                      </div>
                      <span className="text-gray-700 text-sm font-medium flex-1">{item.label}</span>
                    </Link>
                  </motion.div>
                );
              })}

              {/* Profile Link */}
              <motion.div custom={navItems.length} variants={itemVariants}>
                <Link
                  to="/profile"
                  className="flex items-center space-x-3 px-3 py-2.5 rounded-lg hover:bg-amber-50 transition group"
                  onClick={() => setMenuOpen(false)}
                >
                  <div className="w-7 h-7 bg-slate-100 rounded-lg flex items-center justify-center group-hover:bg-amber-100 transition">
                    <FaUserCircle className="w-3.5 h-3.5 text-slate-600 group-hover:text-amber-600" />
                  </div>
                  <span className="text-gray-700 text-sm font-medium flex-1">Profile</span>
                </Link>
              </motion.div>

              {/* Logout Button */}
              <motion.div
                custom={navItems.length + 1}
                variants={itemVariants}
                className="pt-2 mt-2 border-t border-slate-200"
              >
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-3 px-3 py-2.5 rounded-lg hover:bg-red-50 transition group w-full"
                >
                  <div className="w-7 h-7 bg-red-50 rounded-lg flex items-center justify-center group-hover:bg-red-100 transition">
                    <FaSignOutAlt className="w-3.5 h-3.5 text-red-600" />
                  </div>
                  <span className="text-gray-700 text-sm font-medium flex-1 text-left">Logout</span>
                </button>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default MobileHeader;